import { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { styled } from '@material-ui/core/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveIcon from '@mui/icons-material/Remove';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Typography,
  Button,
  CardMedia,
  Avatar,
  Stack,
  IconButton,
  Grid
} from '@material-ui/core';
import LoadingSkeleton from './LoadingSkeleton';

const helpSiteBase = '/static/help-system-guide/';

const defaultHelpTopic = 'virtual_rack_home.html';

// Sidebar widths — full on lg+ where there's a 280px DashboardSidebar alongside,
// narrower on smaller breakpoints so the help panel doesn't dominate the screen.
const SIDEBAR_WIDTH_LG = 450;
const SIDEBAR_WIDTH_SM = 360;

function topicPathFromStep(step) {
  if (!step) {
    return defaultHelpTopic;
  }

  const topic = step
    .replace(/^\//, '')
    .replace(/\.htm$/i, '.html')
    .replace(/\.md$/i, '.html');
  return topic.endsWith('.html') ? topic : `${topic}.html`;
}

// Wrapper that sits in the dashboard's flex row. Collapses to width 0 when the
// panel is hidden so the avatar (position: fixed) stays anchored to the viewport
// corner without reserving space.
const SidebarWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: 0,
  transition: theme.transitions.create('width'),
  overflow: 'hidden',
  ...(open && {
    width: SIDEBAR_WIDTH_SM,
    [theme.breakpoints.up('lg')]: {
      width: SIDEBAR_WIDTH_LG
    }
  })
}));

const PanelShell = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  borderLeft: `1px solid ${theme.palette.divider}`,
  boxShadow: '-6px 0 18px rgba(34, 47, 62, 0.12)'
}));

const avatarStyle = {
  position: 'fixed',
  zIndex: 99999,
  bottom: 20,
  right: 20,
  padding: 0,
  margin: 0,
  minWidth: 40,
  height: 40,
  backgroundColor: 'primary.light',
  '&:hover': { backgroundColor: 'primary.main' }
};

export default function HelpSystem() {
  const [currentDocument, setCurrentDocument] = useState(defaultHelpTopic);
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const setHelpPage = () => {
      // Set current document based on page the user is on
      if (location.search !== ' ') {
        if (searchParams.get('ssh_open')) {
          setCurrentDocument('connect_to_vrack.html');
        } else if (searchParams.get('new_component')) {
          setCurrentDocument('Loading/add_new_component.html');
        } else if (searchParams.get('loading_open')) {
          setCurrentDocument('Loading/load_vrack.html');
        } else if (searchParams.get('step')) {
          setCurrentDocument(topicPathFromStep(searchParams.get('step')));
        } else if (searchParams.get('flight_data_open')) {
          setCurrentDocument('aircraft_data.html');
        } else if (location.pathname) {
          if (location.pathname.startsWith('/snapshots')) {
            setCurrentDocument('snapshots.html');
          } else if (location.pathname.startsWith('/vmtemplates')) {
            setCurrentDocument('vm_templates.html');
          } else if (location.pathname.startsWith('/teams')) {
            setCurrentDocument('team_management.html');
          } else if (location.pathname.includes('/racks/details')) {
            setCurrentDocument('vrack_details.html');
          } else if (location.pathname.includes('/racks')) {
            setCurrentDocument(defaultHelpTopic);
          } else if (
            location.pathname.includes('/team/upload') ||
            location.pathname.includes('/upload')
          ) {
            setCurrentDocument('Loading/loadables.html');
          }
        }
      }
    };

    if (location !== null) {
      setHelpPage();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [location]);

  const helpUrl = `${helpSiteBase}${currentDocument}`;
  const embeddedHelpUrl = `${helpUrl.replace(/\.html$/, '.htm')}?embed=1`;

  return (
    <>
      {collapsed && (
        <Avatar
          data-testid="help-button"
          component={Button}
          onClick={() => {
            setCollapsed(false);
            setLoaded(false);
          }}
          sx={avatarStyle}
        >
          <HelpOutlineIcon fontSize="large" />
        </Avatar>
      )}
      <SidebarWrapper open={!collapsed} aria-hidden={collapsed}>
        {!collapsed && (
          <PanelShell>
            <Box
              sx={{
                padding: 1,
                borderBottom: '3px solid',
                borderColor: 'primary.main'
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ paddingLeft: 1 }} variant="h6">
                  Virtual Rack Help
                </Typography>
                <Stack direction="row" alignItems="center">
                  <IconButton
                    onClick={() => {
                      window.open(helpUrl);
                    }}
                    aria-label="maximize"
                  >
                    <OpenInNewIcon />
                  </IconButton>

                  <IconButton
                    onClick={() => {
                      setCollapsed(true);
                    }}
                    aria-label="minimize"
                  >
                    <RemoveIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Box>

            <Grid item xs sx={{ overflow: 'auto', flex: 1 }}>
              {!loaded && <LoadingSkeleton />}
              <CardMedia
                component="iframe"
                sx={{
                  width: '100%',
                  height: '100%',
                  display: !loaded ? 'none' : '',
                  border: 'none',
                  boxSizing: 'border-box'
                }}
                onLoad={() => {
                  setLoaded(true);
                }}
                image={embeddedHelpUrl}
              />
            </Grid>
          </PanelShell>
        )}
      </SidebarWrapper>
    </>
  );
}
